/**
 * RoomClient — browser-side glue that owns the WebSocket to the Durable Object,
 * drives clock synchronization, suppresses echoes, and surfaces peer events to
 * the player layer. Transport, timers and clock are injected so this is unit
 * testable without a real socket or real time.
 */
import {
  isMessage,
  TIMING,
  type AnyMessage,
  type BufferStateMsg,
  type ClientToServer,
  type ControlMsg,
  type HeartbeatMsg,
  type PlaybackState,
  type ResumeAtMsg,
} from "../shared/protocol.ts";
import { ClockSync } from "./clock.ts";

export interface SocketLike {
  send(data: string): void;
  close(): void;
  readyState: number;
  onopen: (() => void) | null;
  onclose: (() => void) | null;
  onerror: ((e?: unknown) => void) | null;
  onmessage: ((data: string) => void) | null;
}

export interface Scheduler {
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(id: number): void;
  setInterval(fn: () => void, ms: number): number;
  clearInterval(id: number): void;
}

export interface RoomClientOptions {
  clientId: string;
  connect: () => SocketLike;
  now: () => number; // monotonic ms
  uuid: () => string;
  scheduler: Scheduler;
  /** max entries in the echo-suppression ring. */
  recentIdCap?: number;
}

export interface RoomClientEvents {
  onWelcome?: (state: PlaybackState, isReference: boolean, peerConnected: boolean) => void;
  onSnapshot?: (state: PlaybackState) => void;
  onControl?: (msg: ControlMsg) => void;
  onHeartbeat?: (msg: HeartbeatMsg) => void;
  onBufferState?: (msg: BufferStateMsg) => void;
  onResumeAt?: (msg: ResumeAtMsg) => void;
  onPresence?: (peerConnected: boolean, isReference: boolean) => void;
  onClockUpdate?: (offsetMs: number, rttHalfMs: number) => void;
  onConnChange?: (connected: boolean) => void;
}

export class RoomClient {
  readonly clock: ClockSync;
  private socket: SocketLike | null = null;
  private readonly opts: RoomClientOptions;
  private readonly ev: RoomClientEvents;
  private readonly recentIds: string[] = [];
  private readonly recentSet = new Set<string>();
  private readonly recentCap: number;
  private pendingPings = new Map<string, number>(); // id -> cT1
  private steadyTimer: number | null = null;
  private burstTimers: number[] = [];
  private reconnectAttempts = 0;
  private closedByUser = false;
  private closeHandled = false;
  isReference = false;

  constructor(opts: RoomClientOptions, events: RoomClientEvents = {}) {
    this.opts = opts;
    this.ev = events;
    this.recentCap = opts.recentIdCap ?? 256;
    this.clock = new ClockSync({ now: opts.now });
  }

  connect(): void {
    this.closedByUser = false;
    this.openSocket();
  }

  private openSocket(): void {
    const sock = this.opts.connect();
    this.socket = sock;
    this.closeHandled = false;
    sock.onopen = () => this.handleOpen();
    sock.onclose = () => this.handleClose();
    sock.onerror = () => this.handleClose();
    sock.onmessage = (data) => this.handleMessage(data);
  }

  private handleOpen(): void {
    this.reconnectAttempts = 0;
    this.ev.onConnChange?.(true);
    this.rawSend({ t: "hello", clientId: this.opts.clientId });
    this.startClockBurst();
    this.startSteadyPing();
  }

  private handleClose(): void {
    // A real socket failure fires onerror THEN onclose for the same connection.
    // Handle a given connection's teardown exactly once, or we would schedule
    // two reconnects (two parallel sockets, doubled backoff).
    if (this.closeHandled) return;
    this.closeHandled = true;
    // Detach handlers so a late event on the dead socket can't re-enter.
    if (this.socket) {
      this.socket.onopen = this.socket.onclose = this.socket.onerror = this.socket.onmessage = null;
    }
    this.ev.onConnChange?.(false);
    this.stopTimers();
    if (this.closedByUser) return;
    // Exponential backoff reconnect, capped low so a slot freed by a peer's
    // ghost socket (or our own) is reclaimed within a few seconds, not tens.
    const delay = Math.min(8000, 500 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.opts.scheduler.setTimeout(() => {
      if (!this.closedByUser) this.openSocket();
    }, delay);
  }

  disconnect(): void {
    this.closedByUser = true;
    this.stopTimers();
    this.socket?.close();
    this.socket = null;
  }

  private stopTimers(): void {
    if (this.steadyTimer !== null) this.opts.scheduler.clearInterval(this.steadyTimer);
    this.steadyTimer = null;
    for (const id of this.burstTimers) this.opts.scheduler.clearTimeout(id);
    this.burstTimers = [];
  }

  // ── Clock ────────────────────────────────────────────────────────────────
  private startClockBurst(): void {
    for (let i = 0; i < TIMING.CLOCK_BURST_COUNT; i++) {
      const id = this.opts.scheduler.setTimeout(() => this.sendPing(), i * TIMING.CLOCK_BURST_SPACING_MS);
      this.burstTimers.push(id);
    }
  }

  private startSteadyPing(): void {
    this.steadyTimer = this.opts.scheduler.setInterval(() => this.sendPing(), TIMING.CLOCK_STEADY_MS);
  }

  private sendPing(): void {
    const id = this.opts.uuid();
    const cT1 = this.opts.now();
    this.pendingPings.set(id, cT1);
    this.rawSend({ t: "clock_ping", id, cT1 });
  }

  // ── Send helpers ───────────────────────────────────────────────────────────
  private rawSend(msg: ClientToServer): void {
    if (this.socket && this.socket.readyState === 1) {
      this.socket.send(JSON.stringify(msg));
    }
  }

  /** Remember an id we originated so its echo is dropped on the way back. */
  private remember(id: string): void {
    if (this.recentSet.has(id)) return;
    this.recentSet.add(id);
    this.recentIds.push(id);
    if (this.recentIds.length > this.recentCap) {
      const old = this.recentIds.shift()!;
      this.recentSet.delete(old);
    }
  }

  get recentMsgIds(): ReadonlySet<string> {
    return this.recentSet;
  }

  sendControl(msg: ControlMsg): void {
    this.remember(msg.msgId);
    this.rawSend(msg);
  }

  sendHeartbeat(msg: HeartbeatMsg): void {
    this.rawSend(msg);
  }

  sendBufferState(msg: BufferStateMsg): void {
    this.rawSend(msg);
  }

  sendResumeAt(msg: ResumeAtMsg): void {
    this.rawSend(msg);
  }

  // ── Receive ────────────────────────────────────────────────────────────────
  private handleMessage(data: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return; // ignore malformed frames
    }
    if (!isMessage(parsed)) return;
    const msg = parsed as AnyMessage;
    switch (msg.t) {
      case "clock_pong": {
        const cT1 = this.pendingPings.get(msg.id);
        if (cT1 === undefined) return; // unknown/duplicate id
        this.pendingPings.delete(msg.id);
        // Validate the server-stamped fields before they reach the clock — one
        // non-numeric pong would otherwise set the offset to NaN permanently.
        if (!Number.isFinite(msg.sT2) || !Number.isFinite(msg.sT3)) return;
        const cT4 = this.opts.now();
        const sample = this.clock.addSample(cT1, msg.sT2, msg.sT3, cT4);
        if (sample) this.ev.onClockUpdate?.(this.clock.offset, this.clock.rttHalfMs);
        break;
      }
      case "welcome":
        this.isReference = msg.isReference;
        this.ev.onWelcome?.(msg.snapshot, msg.isReference, msg.peerConnected);
        break;
      case "snapshot":
        this.ev.onSnapshot?.(msg.state);
        break;
      case "presence":
        this.isReference = msg.isReference;
        this.ev.onPresence?.(msg.peerConnected, msg.isReference);
        break;
      case "control":
        this.ev.onControl?.(msg);
        break;
      case "heartbeat":
        this.ev.onHeartbeat?.(msg);
        break;
      case "buffer_state":
        this.ev.onBufferState?.(msg);
        break;
      case "resume_at":
        this.ev.onResumeAt?.(msg);
        break;
    }
  }
}
